import { Component } from "react";

/**
 * Global error boundary for the public + authenticated app.
 *
 * A single hook-import typo or lazy-chunk failure used to blank the
 * whole site (see the Feb 2026 `useEffect is not defined` incident in
 * DemoDashboard.jsx). This boundary catches any render-time exception,
 * shows a calm VAULT-styled fallback with Try Again / Return Home, and
 * logs the technical detail to the console for the on-call engineer.
 *
 * Deliberately NOT a hook — React error boundaries must be class
 * components. Keep it dependency-free so it never becomes the source
 * of its own failure.
 */
export default class RootErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: "" };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, message: (error && error.message) || "Unknown error" };
    }

    componentDidCatch(error, info) {
        // Never swallow silently — always log to the console so on-call
        // sees the real stack, even in production.
        console.error("[RootErrorBoundary]", error, info?.componentStack);
    }

    handleReset = () => {
        this.setState({ hasError: false, message: "" });
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div
                data-testid="root-error-boundary"
                style={{
                    minHeight: "100vh",
                    background: "#0F1E3A",
                    color: "#F6F3EC",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "48px 24px",
                    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                }}
            >
                <div style={{ maxWidth: 520, textAlign: "left" }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: "0.24em",
                        textTransform: "uppercase", color: "#E6D5A8", marginBottom: 14,
                    }}>
                        VAULT · Interrupted
                    </div>
                    <h1 style={{
                        fontFamily: "'Cormorant Garamond', ui-serif, Georgia, serif",
                        fontSize: 40, lineHeight: 1.05, letterSpacing: "-0.02em",
                        margin: "0 0 16px", color: "#F6F3EC",
                    }}>
                        Something didn&rsquo;t load correctly.
                    </h1>
                    <p style={{
                        fontSize: 15, lineHeight: 1.55, color: "rgba(246,243,236,0.72)",
                        margin: "0 0 28px",
                    }}>
                        This is on us, not you. Try again — or head back to the VAULT
                        home page. Your data is safe.
                    </p>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <button
                            data-testid="root-error-try-again"
                            onClick={this.handleReset}
                            style={{
                                background: "#E6D5A8", color: "#0F1E3A", border: "none",
                                borderRadius: 999, padding: "10px 18px", fontSize: 13,
                                fontWeight: 700, cursor: "pointer",
                            }}
                        >
                            Try again
                        </button>
                        <a
                            href="/"
                            data-testid="root-error-return-home"
                            style={{
                                background: "transparent", color: "#F6F3EC",
                                border: "1px solid rgba(230,213,168,0.4)", borderRadius: 999,
                                padding: "10px 18px", fontSize: 13, fontWeight: 600,
                                textDecoration: "none",
                            }}
                        >
                            Return to VAULT
                        </a>
                    </div>
                    {process.env.NODE_ENV !== "production" && this.state.message && (
                        <pre style={{
                            marginTop: 32, padding: 14, borderRadius: 10,
                            background: "rgba(230,213,168,0.06)",
                            border: "1px solid rgba(230,213,168,0.14)",
                            fontSize: 11, color: "rgba(246,243,236,0.72)",
                            whiteSpace: "pre-wrap", wordBreak: "break-word",
                        }}>
                            {this.state.message}
                        </pre>
                    )}
                </div>
            </div>
        );
    }
}
